// Small pure helpers of the composition root (main.ts): view inputs derived from the loaded data.

import { hexAtLonLat } from '../core/grid.ts';
import type { FieldArray } from '../core/gridfile.ts';
import type { SourceId } from '../core/schema.ts';
import type { SmallLake } from '../data/smalllakes.ts';
import type { Services } from '../services/api.ts';
import type { ActorLabel, PhysicalGrid, QualityTier } from '../view/index.ts';
import type { World } from '../world/api.ts';

/** The per-hex fields the terrain mesher reads (src/view/hexdata.ts PhysicalGrid). */
const PHYSICAL_FIELDS = ['terrain', 'feature', 'hill', 'elev', 'elev_max', 'relief', 'river', 'river_size', 'ice', 'land', 'lat'] as const;

const TIERS: readonly QualityTier[] = ['low', 'med', 'high'];
const DEFAULT_TIER: QualityTier = 'med';

export function physicalGrid(fields: Map<string, FieldArray>): PhysicalGrid {
  const out: Record<string, FieldArray> = {};
  for (const name of PHYSICAL_FIELDS) {
    const field = fields.get(name);
    if (!field) {
      throw new Error(`grid: missing physical field ${name}`);
    }
    out[name] = field;
  }
  return out as unknown as PhysicalGrid;
}

/** Colours by actor index (index 0 = no actor), for HexData.setActorColors. */
export function actorColors(world: Pick<World, 'actorAt'>): Array<{ primary: string; secondary: string } | null> {
  const out: Array<{ primary: string; secondary: string } | null> = [];
  for (let i = 0; ; i++) {
    const actor = world.actorAt(i);
    if (!actor && i > 0) {
      return out;
    }
    out.push(actor?.colors ?? null);
  }
}

/**
 * What the view labels: every actor with its name, colours, sources and capital hex. Countries
 * cite Country.sources; other actors cite their record in world.json (`curated`), and one with
 * no record gets none, which the view then counts as an unsourced marker.
 */
export function actorLabels(world: Pick<World, 'actorAt' | 'countries'>, curated: ReadonlyArray<{ id: string; sources: SourceId[] }>): ActorLabel[] {
  const country = new Map(world.countries.map((c) => [c.id, c]));
  const recorded = new Map(curated.map((a) => [a.id, a.sources]));
  const out: ActorLabel[] = [];

  for (let i = 1; ; i++) {
    const actor = world.actorAt(i);
    if (!actor) {
      return out;
    }
    const c = country.get(i);

    // `id` links a city's country (City.iso3) to its index: without it every banner plate takes
    // actor 0's colours. `cityState` draws the ring and banner on a sub-hex state's capital hex.
    out.push({
      index: i,
      id: actor.id,
      cityState: c?.city_state === true,
      name: actor.name,
      colors: actor.colors,
      sources: c?.sources ?? recorded.get(actor.id) ?? [],
      capitalHex: c?.capital?.hex,
    });
  }
}

/** `?tier=low|med|high`, else the default. */
export function tierOf(query: string): QualityTier {
  const value = new URLSearchParams(query).get('tier') as QualityTier | null;
  return value && TIERS.includes(value) ? value : DEFAULT_TIER;
}

/**
 * `services` whose hover card also cites the lakes smaller than a hex that lie in the hovered hex
 * (the hex under the lake's point), so a water decal never appears without its sources.
 */
export function withSmallLakes(services: Services, lakes: readonly SmallLake[]): Services {
  const cited = new Map<number, SourceId[]>();
  for (const lake of lakes) {
    const hex = hexAtLonLat(lake.lon, lake.lat);
    cited.set(hex, [...(cited.get(hex) ?? []), ...lake.sources]);
  }
  if (cited.size === 0) {
    return services;
  }

  const hexInfo = (hex: number) => {
    const detail = services.world.hexInfo(hex);
    const extra = cited.get(hex);
    return extra ? { ...detail, sources: [...new Set([...detail.sources, ...extra])] } : detail;
  };
  return { ...services, world: { ...services.world, hexInfo } };
}
